/**
 * Lloyds (LBG) invoice download.
 *
 * The invoice URL comes back from the API, so it is never rendered into an
 * `href` — the PDF is fetched and handed to a blob URL instead, which keeps an
 * untrusted URL out of the DOM. The tab is opened *before* the fetch because
 * browsers only allow `window.open` inside the click handler's own task; opening
 * it after the `await` gets caught by the popup blocker.
 *
 * Shared by the request summary ("View details") and the payment-success card
 * ("Download invoice") so both behave identically.
 *
 * @returns `true` when the invoice was handed to a tab (or the browser was
 * navigated to it), `false` when it could not be retrieved — the caller decides
 * how to tell the payer.
 */
export const downloadInvoice = async (invoiceLink: string): Promise<boolean> => {
  // Open window immediately to avoid popup blocker
  const newTab = window.open("", "_blank");

  if (!newTab) {
    // If popup was blocked, fall back to direct navigation
    window.location.href = invoiceLink;
    return true;
  }

  try {
    // Show loading message in the new tab
    newTab.document.write("Loading invoice...");

    // Fetch the PDF as a blob
    const response = await fetch(invoiceLink);
    if (!response.ok) throw new Error("Failed to download invoice");

    const blobUrl = URL.createObjectURL(await response.blob());

    // Navigate the already-open tab to the blob URL
    newTab.location.href = blobUrl;

    // Clean up the blob URL after a delay to ensure the tab has loaded
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 1000);

    return true;
  } catch (error) {
    // Close the tab if we opened it but got an error
    if (!newTab.closed) newTab.close();
    return false;
  }
};
